import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './VKAccountsManager.css';

const STAGE_TITLES = {
    pending: 'подготовка',
    counterparties: 'контрагенты',
    contracts: 'договоры',
    platforms: 'площадки',
    creatives: 'креативы',
    statistics: 'статистика',
    acts: 'акты',
    completed: 'завершение'
};

const VKAccountsManager = ({ onAccountSwitch }) => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddEditModal, setShowAddEditModal] = useState(false);
    const [currentAccount, setCurrentAccount] = useState(null);
    const [accountName, setAccountName] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [message, setMessage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [syncingAccountId, setSyncingAccountId] = useState(null);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [syncProgress, setSyncProgress] = useState({
        status: 'idle', // 'idle', 'syncing', 'success', 'error'
        message: '',
        results: null
    });
    const [activeJobId, setActiveJobId] = useState(null);
    const pollIntervalRef = useRef(null);

    useEffect(() => {
        fetchAccounts();
    }, []);

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const response = await api.get('/vk-accounts/');
            // Фильтруем только активные кабинеты
            const activeAccounts = Array.isArray(response.data) 
                ? response.data.filter(acc => acc.is_active !== false)
                : [];
            setAccounts(activeAccounts);
        } catch (error) {
            console.error('Error fetching VK accounts:', error);
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    };

    const clearJobPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            clearJobPolling();
        };
    }, []);

    const buildResultsFromProgress = (progress) => ({
        counterparties: progress?.counterparties || 0,
        contracts: progress?.contracts || 0,
        platforms: progress?.platforms || 0,
        creatives: progress?.creatives || 0,
        statistics: progress?.statistics || 0,
        acts: progress?.acts || 0
    });

    const applyJobUpdate = (job) => {
        if (!job) {
            return;
        }
        const progress = job.progress || {};
        const stageKey = progress.current_stage || 'pending';
        const stageTitle = STAGE_TITLES[stageKey] || 'подготовка';
        const results = buildResultsFromProgress(progress);

        let status = 'syncing';
        let message = `Синхронизация: ${stageTitle}...`;

        if (job.status === 'success') {
            status = 'success';
            message = 'Синхронизация завершена!';
            clearJobPolling();
            setActiveJobId(null);
            fetchAccounts();
        } else if (job.status === 'failed') {
            status = 'error';
            message = job.error_message || 'Ошибка синхронизации';
            clearJobPolling();
            setActiveJobId(null);
        }

        setSyncProgress({
            status,
            message,
            results
        });
    };

    const fetchJobStatus = async (jobId) => {
        if (!jobId) return;
        try {
            const { data } = await api.get(`/sync-all/jobs/${jobId}`);
            applyJobUpdate(data);
        } catch (error) {
            console.error('Ошибка получения статуса синхронизации:', error);
            clearJobPolling();
            setSyncProgress(prev => ({
                status: 'error',
                message: 'Не удалось получить статус синхронизации.',
                results: prev?.results || null
            }));
        }
    };

    const startJobPolling = (jobId) => {
        if (!jobId) return;
        clearJobPolling();
        setActiveJobId(jobId);
        fetchJobStatus(jobId);
        pollIntervalRef.current = setInterval(() => fetchJobStatus(jobId), 4000);
    };

    const handleAddEditAccount = async () => {
        if (!accountName.trim() || !apiKey.trim()) {
            setMessage({ type: 'error', text: 'Заполните все поля.' });
            return;
        }

        setSaving(true);
        setMessage(null);
        try {
            const accountData = { account_name: accountName, vk_ord_token: apiKey };

            if (currentAccount) {
                await api.put(`/vk-accounts/${currentAccount.id}`, accountData);
                setMessage({ type: 'success', text: 'Кабинет успешно обновлен!' });
                setShowAddEditModal(false);
                setAccountName('');
                setApiKey('');
                setCurrentAccount(null);
                fetchAccounts();
            } else {
                // Создаем новый кабинет
                await api.post('/vk-accounts/', accountData);
                
                // Очищаем форму и закрываем модалку добавления
                setAccountName('');
                setApiKey('');
                setCurrentAccount(null);
                setShowAddEditModal(false);
                
                // Сразу запускаем полную синхронизацию (показываем модалку синхронизации)
                await runFullSync();
                
                // После синхронизации обновляем список кабинетов
                fetchAccounts();
            }
        } catch (error) {
            console.error('Error saving VK account:', error);
            const errorMsg = error.response?.data?.detail || 'Неизвестная ошибка при сохранении кабинета.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setSaving(false);
        }
    };

    const runFullSync = async () => {
        
        clearJobPolling();
        setShowSyncModal(true);
        setSyncProgress({
            status: 'syncing',
            message: 'Запускаем синхронизацию данных из VK ORD...',
            results: null
        });

        try {
            const response = await api.post('/sync-all/start');
            const jobId = response.data?.job_id;
            const jobPayload = response.data?.job;

            if (!jobId) {
                throw new Error('job_id отсутствует в ответе сервера');
            }

            if (jobPayload) {
                applyJobUpdate(jobPayload);
            }

            if (jobPayload && ['success', 'failed'].includes(jobPayload.status)) {
                setActiveJobId(null);
                return;
            }

            startJobPolling(jobId);
        } catch (error) {
            console.error('❌ runFullSync: ошибка запуска синхронизации:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Ошибка синхронизации';
            setSyncProgress({
                status: 'error',
                message: errorMsg,
                results: null
            });
            clearJobPolling();
        }
    };

    const handleDeleteAccount = async (accountId) => {
        const account = accounts.find(acc => acc.id === accountId);
        const isOnlyAccount = accounts.length === 1;
        
        // Если это единственный кабинет - особое предупреждение
        const confirmMessage = isOnlyAccount
            ? 'Вы уверены, что хотите удалить ЕДИНСТВЕННЫЙ кабинет? Все ваши данные (креативы, договоры, контрагенты, площадки) будут удалены без возможности восстановления!'
            : 'Вы уверены, что хотите удалить этот рекламный кабинет? Все связанные данные будут удалены.';
        
        if (!window.confirm(confirmMessage)) {
            return;
        }
        
        setMessage(null);
        try {
            await api.delete(`/vk-accounts/${accountId}`);
            setMessage({ type: 'success', text: 'Кабинет успешно удален.' });
            fetchAccounts();
            
            // Если удалили последний кабинет - обновить родительский компонент
            if (isOnlyAccount && onAccountSwitch) {
                onAccountSwitch();
            }
        } catch (error) {
            console.error('Error deleting VK account:', error);
            const errorMsg = error.response?.data?.detail || 'Неизвестная ошибка при удалении кабинета.';
            setMessage({ type: 'error', text: errorMsg });
        }
    };

    const handleSwitchAccount = async (accountId) => {
        setMessage(null);
        try {
            await api.post(`/vk-accounts/${accountId}/switch`);
            setMessage({ type: 'success', text: 'Активный кабинет успешно изменен!' });
            fetchAccounts();
            if (onAccountSwitch) {
                onAccountSwitch();
            }
        } catch (error) {
            console.error('Error switching VK account:', error);
            const errorMsg = error.response?.data?.detail || 'Неизвестная ошибка при переключении кабинета.';
            setMessage({ type: 'error', text: errorMsg });
        }
    };

    const handleSyncAccount = async (accountId) => {
        
        setSyncingAccountId(accountId);
        await runFullSync();
        setSyncingAccountId(null);
    };

    if (loading) {
        return (
            <div className="vk-accounts-manager-loading">
                <div className="spinner"></div>
                <p>Загрузка VK кабинетов...</p>
            </div>
        );
    }

    return (
        <div className="vk-accounts-manager">
            {message && (
                <div className={`alert-box alert-${message.type}`}>
                    <span className="alert-icon">
                        {message.type === 'success' ? '✅' : '❌'}
                    </span>
                    <span className="alert-text">{message.text}</span>
                </div>
            )}

            <div className="accounts-list">
                {accounts.length === 0 ? (
                    <div className="empty-state">
                        <p>У вас пока нет подключенных VK рекламных кабинетов.</p>
                        <button className="btn btn-primary add-account-btn" onClick={() => setShowAddEditModal(true)}>
                            <i className="fas fa-plus"></i> Добавить первый кабинет
                        </button>
                    </div>
                ) : (
                    <>
                        {accounts.map(account => (
                            <div key={account.id} className={`account-card ${account.is_default ? 'default-account' : ''}`}>
                                <div className="account-header">
                                    <h3>{account.account_name}</h3>
                                    {account.is_default && <span className="default-badge"><i className="fas fa-star"></i> По умолчанию</span>}
                                </div>
                                <div className="account-details">
                                    <p><strong>ID компании:</strong> {account.company_id || 'Неизвестно'}</p>
                                    <p><strong>Название компании:</strong> {account.company_name || 'Неизвестно'}</p>
                                    <p><strong>ИНН:</strong> {account.company_inn || 'Неизвестно'}</p>
                                    <p><strong>Статус:</strong> {account.is_active ? '✅ Активен' : '❌ Неактивен'}</p>
                                    <p><strong>Последняя синхронизация:</strong> {account.last_synced_at ? new Date(account.last_synced_at).toLocaleString() : 'Никогда'}</p>
                                </div>
                                <div className="account-actions">
                                    {!account.is_default && (
                                        <button className="btn btn-secondary" onClick={() => handleSwitchAccount(account.id)}>
                                            <i className="fas fa-sync-alt"></i> Сделать основным
                                        </button>
                                    )}
                                    <button 
                                        className="btn btn-outline" 
                                        onClick={() => {
                                            
                                            handleSyncAccount(account.id);
                                        }} 
                                        disabled={syncingAccountId === account.id}
                                    >
                                        {syncingAccountId === account.id ? <><i className="fas fa-spinner fa-spin"></i> Синхронизация...</> : <><i className="fas fa-sync"></i> Синхронизировать</>}
                                    </button>                                    <button 
                                        className="btn btn-icon" 
                                        onClick={() => { 
                                            setCurrentAccount(account); 
                                            setAccountName(account.account_name); 
                                            setApiKey(account.vk_ord_token); 
                                            setShowAddEditModal(true); 
                                        }}
                                    >
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button 
                                        className="btn btn-icon btn-danger" 
                                        onClick={() => handleDeleteAccount(account.id)} 
                                        disabled={account.is_default && accounts.length > 1}
                                        title={
                                            accounts.length === 1 
                                                ? 'Удалить кабинет (единственный)' 
                                                : account.is_default 
                                                    ? 'Нельзя удалить активный кабинет (сначала переключитесь на другой)' 
                                                    : 'Удалить кабинет'
                                        }
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button className="btn btn-primary add-account-btn" onClick={() => setShowAddEditModal(true)}>
                            <i className="fas fa-plus"></i> Добавить новый кабинет
                        </button>
                    </>
                )}
            </div>

            {showAddEditModal && (
                <div className="modal-overlay" onClick={() => { setShowAddEditModal(false); setCurrentAccount(null); setAccountName(''); setApiKey(''); setMessage(null); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{currentAccount ? 'Редактировать кабинет' : 'Добавить новый VK кабинет'}</h2>
                        {message && (
                            <div className={`alert-box alert-${message.type}`}>
                                <span className="alert-icon">
                                    {message.type === 'success' ? '✅' : '❌'}
                                </span>
                                <span className="alert-text">{message.text}</span>
                            </div>
                        )}
                        <div className="form-field">
                            <label>Название кабинета:</label>
                            <input
                                type="text"
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                                placeholder="Например: Основной кабинет, Тестовый аккаунт"
                            />
                        </div>
                        <div className="form-field">
                            <label>VK ORD API Ключ:</label>
                            <input
                                type="password"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="Введите ваш VK ORD API ключ"
                            />
                        </div>
                        <div className="modal-actions">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => { 
                                    setShowAddEditModal(false); 
                                    setCurrentAccount(null); 
                                    setAccountName(''); 
                                    setApiKey(''); 
                                    setMessage(null); 
                                }}
                            >
                                Отмена
                            </button>
                            <button 
                                className="btn btn-primary" 
                                onClick={handleAddEditAccount} 
                                disabled={saving}
                            >
                                {saving ? <><i className="fas fa-spinner fa-spin"></i> Сохранение...</> : 'Сохранить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Модальное окно синхронизации */}
            {showSyncModal && (
                <div className="modal-overlay">
                    <div className="modal-content sync-modal">
                        <div className="sync-modal-header">
                            {syncProgress.status === 'syncing' && (
                                <>
                                    <div className="sync-spinner">
                                        <i className="fas fa-sync fa-spin"></i>
                                    </div>
                                    <h2>Синхронизация данных</h2>
                                    <p>Пожалуйста, подождите. Загружаем данные из VK ORД...</p>
                                </>
                            )}
                            {syncProgress.status === 'success' && (
                                <>
                                    <div className="sync-success-icon">✅</div>
                                    <h2>Синхронизация завершена!</h2>
                                </>
                            )}
                            {syncProgress.status === 'error' && (
                                <>
                                    <div className="sync-error-icon">❌</div>
                                    <h2>Ошибка синхронизации</h2>
                                </>
                            )}
                        </div>

                        <div className="sync-modal-body">
                            {syncProgress.results && (
                                <div className="sync-results">
                                    <div className="sync-result-item">
                                        <span className="sync-result-icon">📋</span>
                                        <span className="sync-result-label">Контрагенты:</span>
                                        <span className="sync-result-count">{syncProgress.results.counterparties}</span>
                                    </div>
                                    <div className="sync-result-item">
                                        <span className="sync-result-icon">📄</span>
                                        <span className="sync-result-label">Договоры:</span>
                                        <span className="sync-result-count">{syncProgress.results.contracts}</span>
                                    </div>
                                    <div className="sync-result-item">
                                        <span className="sync-result-icon">📡</span>
                                        <span className="sync-result-label">Площадки:</span>
                                        <span className="sync-result-count">{syncProgress.results.platforms}</span>
                                    </div>
                                    <div className="sync-result-item">
                                        <span className="sync-result-icon">🎨</span>
                                        <span className="sync-result-label">Креативы:</span>
                                        <span className="sync-result-count">{syncProgress.results.creatives}</span>
                                    </div>
                                    <div className="sync-result-item">
                                        <span className="sync-result-icon">📊</span>
                                        <span className="sync-result-label">Статистика:</span>
                                        <span className="sync-result-count">{syncProgress.results.statistics}</span>
                                    </div>
                                    <div className="sync-result-item">
                                        <span className="sync-result-icon">📑</span>
                                        <span className="sync-result-label">Акты:</span>
                                        <span className="sync-result-count">{syncProgress.results.acts}</span>
                                    </div>
                                </div>
                            )}
                            <p className="sync-message">{syncProgress.message}</p>
                        </div>

                        {syncProgress.status !== 'syncing' && (
                            <div className="sync-modal-footer">
                                <button 
                                    className="btn btn-primary"
                                    onClick={() => {
                                        clearJobPolling();
                                        setShowSyncModal(false);
                                        window.location.reload();
                                    }}
                                >
                                    Готово
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default VKAccountsManager;
